import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ClipboardCheck, LoaderCircle, UploadCloud, XCircle } from 'lucide-react'
import { api, getApiErrorMessage } from '../../lib/api'
import { useToast } from '../../ui/ToastProvider'

const STATUS_LABELS = {
  PENDING: '진행 중',
  APPROVED: '승인',
  REJECTED: '반려',
  COMPLETED: '완료',
  CANCELLED: '취소',
  EXPIRED: '기간 만료',
}

function unwrap(response, fallback) {
  return response?.data?.data ?? response?.data ?? fallback
}

function formatDate(value) {
  if (!value) return '-'
  return new Date(value).toLocaleString('ko-KR')
}

export default function OrgLibraryWorkflowPanel({
  mode,
  apiRoot,
  rootOrgId,
  folderTree,
  canManage,
  onUpload,
  onOpenFile,
}) {
  const { show } = useToast()
  const [items, setItems] = useState([])
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [folderId, setFolderId] = useState('')
  const [assignedToUserId, setAssignedToUserId] = useState('')
  const [title, setTitle] = useState('')
  const [expiresInDays, setExpiresInDays] = useState(7)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const requests = [
        api.get(`${apiRoot}/workspaces/${rootOrgId}/${mode === 'REVIEWS' ? 'reviews' : 'upload-requests'}`, {
          __silent: true, __suppressErrorPageRedirect: true,
        }),
      ]
      if (mode === 'REQUESTS' && canManage) {
        requests.push(api.get(`${apiRoot}/workspaces/${rootOrgId}/member-targets`, {
          __silent: true, __suppressErrorPageRedirect: true,
        }))
      }
      const [itemResponse, memberResponse] = await Promise.all(requests)
      setItems(unwrap(itemResponse, []))
      if (memberResponse) setMembers(unwrap(memberResponse, []))
    } catch (error) {
      show(getApiErrorMessage(error, '요청 현황을 불러오지 못했습니다.'), 'error')
    } finally {
      setLoading(false)
    }
  }, [apiRoot, canManage, mode, rootOrgId, show])

  useEffect(() => { load() }, [load])

  const managedFolders = useMemo(
    () => (folderTree || []).filter((folder) => folder.manageable),
    [folderTree],
  )

  const createRequest = async (event) => {
    event.preventDefault()
    if (!assignedToUserId || !title.trim()) return
    setBusy(true)
    try {
      await api.post(`${apiRoot}/workspaces/${rootOrgId}/upload-requests`, {
        folderId: folderId ? Number(folderId) : null,
        assignedToUserId: Number(assignedToUserId),
        title: title.trim(),
        expiresInDays: Number(expiresInDays),
      })
      show('업로드 요청을 보냈습니다.', 'success')
      setTitle('')
      setAssignedToUserId('')
      await load()
    } catch (error) {
      show(getApiErrorMessage(error, '업로드 요청을 보내지 못했습니다.'), 'error')
    } finally {
      setBusy(false)
    }
  }

  const cancelRequest = async (requestId) => {
    setBusy(true)
    try {
      await api.delete(`${apiRoot}/workspaces/${rootOrgId}/upload-requests/${requestId}`)
      show('업로드 요청을 취소했습니다.', 'success')
      await load()
    } catch (error) {
      show(getApiErrorMessage(error, '업로드 요청을 취소하지 못했습니다.'), 'error')
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return <div className="flex min-h-[420px] items-center justify-center"><LoaderCircle className="h-7 w-7 animate-spin text-slate-400" /></div>
  }

  if (mode === 'REVIEWS') {
    return (
      <div>
        {!items.length ? (
          <div className="flex min-h-[360px] flex-col items-center justify-center text-sm text-slate-500">
            <ClipboardCheck className="mb-3 h-10 w-10 text-slate-300" />검토 요청이 없습니다.
          </div>
        ) : (
          <ol className="divide-y divide-slate-200 border-y border-slate-200">
            {items.map((review) => (
              <li key={review.id}>
                <button
                  type="button"
                  onClick={() => onOpenFile({ id: review.fileId, name: review.fileName })}
                  className="grid w-full gap-2 py-4 text-left hover:bg-slate-50 sm:grid-cols-[minmax(0,1fr)_160px_130px]"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900">{review.fileName}</p>
                    <p className="mt-1 truncate text-xs text-slate-500">요청자 {review.requestedByName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">담당자</p>
                    <p className="mt-1 text-sm text-slate-700">{review.assignedToName}</p>
                  </div>
                  <div className="sm:text-right">
                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${review.status === 'PENDING' ? 'bg-amber-50 text-amber-700' : review.status === 'APPROVED' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                      {STATUS_LABELS[review.status] || '검토'}
                    </span>
                    <p className="mt-1 text-xs text-slate-400">{formatDate(review.createdAt)}</p>
                  </div>
                </button>
              </li>
            ))}
          </ol>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {canManage && (
        <form onSubmit={createRequest} className="grid gap-2 border-b border-slate-200 pb-5 lg:grid-cols-[minmax(150px,1fr)_minmax(150px,1fr)_minmax(220px,2fr)_100px_auto]">
          <select value={folderId} onChange={(event) => setFolderId(event.target.value)} className="h-10 min-w-0 rounded-md border border-slate-300 bg-white px-3 text-sm">
            <option value="">최상위</option>
            {managedFolders.map((folder) => <option key={folder.id} value={folder.id}>{folder.name}</option>)}
          </select>
          <select value={assignedToUserId} onChange={(event) => setAssignedToUserId(event.target.value)} className="h-10 min-w-0 rounded-md border border-slate-300 bg-white px-3 text-sm">
            <option value="">업로드 담당자</option>
            {members.map((member) => <option key={member.principalId} value={member.principalId}>{member.label}</option>)}
          </select>
          <input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={160} placeholder="요청 제목" className="h-10 min-w-0 rounded-md border border-slate-300 px-3 text-sm" />
          <select value={expiresInDays} onChange={(event) => setExpiresInDays(event.target.value)} aria-label="요청 기한" className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm">
            <option value={3}>3일</option>
            <option value={7}>7일</option>
            <option value={14}>14일</option>
            <option value={30}>30일</option>
          </select>
          <button type="submit" disabled={busy || !assignedToUserId || !title.trim()} className="h-10 rounded-md bg-sgez-primary px-4 text-sm font-semibold text-white disabled:opacity-50">요청</button>
        </form>
      )}

      {!items.length ? (
        <div className="flex min-h-[300px] flex-col items-center justify-center text-sm text-slate-500">
          <UploadCloud className="mb-3 h-10 w-10 text-slate-300" />업로드 요청이 없습니다.
        </div>
      ) : (
        <ol className="divide-y divide-slate-200 border-y border-slate-200">
          {items.map((request) => (
            <li key={request.id} className="grid gap-3 py-4 sm:grid-cols-[minmax(0,1fr)_160px_150px_auto] sm:items-center">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-900">{request.title}</p>
                <p className="mt-1 truncate text-xs text-slate-500">{request.folderName} · 요청자 {request.requestedByName}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">담당자</p>
                <p className="mt-1 text-sm text-slate-700">{request.assignedToName}</p>
              </div>
              <div>
                <span className="text-xs font-medium text-slate-600">{STATUS_LABELS[request.status] || '요청'}</span>
                <p className="mt-1 text-xs text-slate-400">{formatDate(request.expiresAt)}</p>
              </div>
              <div className="flex justify-end gap-1">
                {request.canUpload && (
                  <button type="button" onClick={() => onUpload(request)} disabled={busy} className="inline-flex h-9 items-center gap-1 rounded-md bg-sgez-primary px-3 text-sm font-semibold text-white">
                    <UploadCloud className="h-4 w-4" />업로드
                  </button>
                )}
                {request.canCancel && (
                  <button type="button" onClick={() => cancelRequest(request.id)} disabled={busy} title="요청 취소" aria-label="요청 취소" className="inline-flex h-9 w-9 items-center justify-center rounded-md text-red-600 hover:bg-red-50">
                    <XCircle className="h-4 w-4" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
